import { authRequest, withAuthCookieLock } from './authTransport';

/**
 * API client for Geniac API Gateway.
 *
 * Access token stored in memory (not localStorage).
 * Refresh token in HttpOnly cookie (handled by browser automatically).
 * On 401: attempt one silent refresh, then retry.
 * Refresh is deduplicated — concurrent 401s share one refresh call.
 *
 * Session-expiry resilience (#294, #110):
 * - Refresh outcomes are typed (success / expired / transient) so callers
 *   can distinguish "re-OTP will fix this" from "hiccup, retry silently".
 * - A 401 is retried via refresh even when `accessToken` is null — a single
 *   transient refresh blip used to clear the token and permanently break the
 *   tab until reload. The refresh cookie is usually still valid.
 * - On confirmed expiry (refresh returns 401) the first request opens a
 *   blocking re-OTP modal via `reauthOrWait()`; concurrent 401s and new
 *   requests issued while the modal is open share/gate on the same promise
 *   instead of firing speculative 401s.
 * - A 401 *after* a successful refresh is an authorization issue (RLS /
 *   permissions), not session expiry — it throws `Unauthorized` without
 *   popping the modal, and a non-401 retry error propagates accurately
 *   instead of being converted into `Session expired`.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

// User-facing wording for a refresh that failed transiently (server hiccup /
// timeout). Kept out of the auth internals so callers can show it directly.
export const TRANSIENT_REFRESH_MESSAGE =
  "We couldn't verify your session right now. Please try again.";

// Bounded backoff after a transient refresh failure, so successive 401-producing
// polls don't each fire a fresh refresh against an outage.
const REFRESH_BACKOFF_MS = 5_000;

let accessToken: string | null = null;
let sessionEmail: string | null = null;
// Token absence can mean initial restoration or a transient refresh failure.
// Explicit sign-out is different: a still-valid cookie must not revive this tab.
let signedOut = false;
let refreshPromise: Promise<RefreshOutcome> | null = null;
let refreshCooldownUntil = 0;
// Incremented on sign-out. Requests capture it at entry and refuse to install
// refresh results or retry once it moves — a delayed 401 from before sign-out
// can otherwise revive the old session and replay a write.
let authGeneration = 0;

// --- Reauth signal (session-expired modal) --------------------------------

let pendingReauth: Promise<boolean> | null = null;
let resolveReauth: ((ok: boolean) => void) | null = null;
let onSessionExpired: (() => void) | null = null;
let onSessionChanged: (() => void) | null = null;

export function setSessionChangedHandler(fn: (() => void) | null) {
  onSessionChanged = fn;
}

export function setSessionExpiredHandler(fn: (() => void) | null) {
  onSessionExpired = fn;
}

// Called by the modal once the user completes (or cancels) re-auth.
export function resumeAfterReauth(ok: boolean) {
  resolveReauth?.(ok);
  resolveReauth = null;
  pendingReauth = null;
}

/**
 * True while a request is parked behind the session-expired modal.
 *
 * `request()` awaits `pendingReauth`, and that wait is HUMAN-paced — it ends
 * when someone reads an OTP out of their email, not when the network settles.
 * Any caller imposing its own client-side deadline has to be able to tell that
 * apart from a stall, or it times out a request that was never stuck and
 * reports a transport failure for what is actually a login prompt. The
 * discovery search deadline is the current caller; see `useDiscoveryWorkspace`.
 */
export function isReauthPending(): boolean {
  return pendingReauth !== null;
}

/**
 * Invalidate the current session: drop the token and bump the generation so
 * any in-flight request, refresh result, or retry carrying the old generation
 * is discarded instead of replaying under stale credentials. Call on sign-out.
 */
export function invalidateAuth(): void {
  authGeneration += 1;
  accessToken = null;
  sessionEmail = null;
  signedOut = true;
  refreshCooldownUntil = 0;
}

async function reauthOrWait(): Promise<boolean> {
  if (!onSessionExpired) {
    // Hang protection: if no handler is registered, fail fast rather than
    // hanging an unresolved promise forever. AuthProvider registers the
    // handler in a useEffect on mount; if we get here, something is wrong.
    throw new Error('Session expired (no reauth handler registered)');
  }
  if (!pendingReauth) {
    // Capture the promise in a local before invoking the handler: the
    // handler may resolve synchronously (e.g. the /login skip path calls
    // resumeAfterReauth(false) inline), which nulls the module-level slot.
    const p = new Promise<boolean>((resolve) => {
      resolveReauth = resolve;
    });
    pendingReauth = p;
    try {
      onSessionExpired();
    } catch (e) {
      // A throwing handler must not brick the pipeline: every future request
      // gates on pendingReauth, so drop the orphaned promise before surfacing.
      pendingReauth = null;
      resolveReauth = null;
      throw e;
    }
    return p;
  }
  return pendingReauth;
}

/** Test-only reset for module-level auth state between unit tests. */
export function __testResetApiState() {
  accessToken = null;
  refreshPromise = null;
  pendingReauth = null;
  resolveReauth = null;
  onSessionExpired = null;
  onSessionChanged = null;
  sessionEmail = null;
  signedOut = false;
  authGeneration = 0;
  refreshCooldownUntil = 0;
}

export function setAccessToken(token: string | null, email?: string) {
  accessToken = token;
  if (!token) sessionEmail = null;
  else {
    signedOut = false;
    if (email) sessionEmail = email.toLowerCase().trim();
  }
}

function checkGeneration(gen: number): void {
  if (gen !== authGeneration) throw new Error('Session expired');
}

// Aborting one page request must not cancel the shared refresh/modal for others.
function waitWithSignal<T>(pending: Promise<T>, signal?: AbortSignal | null): Promise<T> {
  if (!signal) return pending;
  if (signal.aborted) return Promise.reject(signal.reason);
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason);
    signal.addEventListener('abort', abort, {once: true});
    pending.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

function tokenEmail(token: string): string | null {
  try {
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(encoded), char => char.charCodeAt(0));
    const payload = JSON.parse(new TextDecoder().decode(bytes));
    return typeof payload.email === 'string' && payload.email ? payload.email : null;
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Error thrown for any non-2xx response carrying a JSON body. Subclass
 * of `Error` so existing `catch (e)` sites still work, plus a `status`
 * + `code` field for callers that want to branch on the typed code
 * the server emitted (e.g. the retry button surfaces a tailored
 * "A retry is already running" inline message on
 * `code: extraction_already_running`).
 *
 * UX-5d: before this class, the request() helper discarded the typed
 * code and rethrew a generic Error with just the message. The retry
 * 409 was caught with a bare `console.error('Restart failed', e)` —
 * the user saw the button spin for 5s and revert with zero
 * explanation. With this shape, components can read `e.code` and
 * decide whether to render an inline hint or a generic toast.
 */
export class ApiResponseError extends Error {
  readonly status: number;
  readonly code: string | null;
  /**
   * Full parsed JSON body of the error response, when one was returned.
   * Lets callers read route-specific structured fields (e.g. the
   * `/ai/graph-search` 504 carries `advice`, `elapsed_s`, `query_id`).
   * Typed as a loose record because shapes vary per endpoint — the
   * server contract is the source of truth.
   */
  readonly body: Record<string, unknown> | null;
  constructor(
    message: string,
    status: number,
    code: string | null,
    body: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ApiResponseError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** Keep HTTP status even if the service returns empty or unexpected JSON. */
export async function readApiResponseError(response: Response): Promise<ApiResponseError> {
  const parsed: unknown = await response.json().catch(() => null);
  const body = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    ? parsed as Record<string, unknown> : null;
  return new ApiResponseError(
    typeof body?.error === 'string' && body.error ? body.error : `HTTP ${response.status}`,
    response.status,
    typeof body?.code === 'string' ? body.code : null,
    body,
  );
}

type RefreshOutcome =
  | { kind: 'success'; email: string | null }
  | { kind: 'expired' } // /auth/refresh returned 401 → real session expiry
  | { kind: 'transient' }; // 5xx / network / timeout → keep token, no modal

type Parse<T> = (response: Response) => Promise<T>;
const parseJson = <T,>(response: Response) => response.json() as Promise<T>;

/**
 * Retry a request with the current (freshly refreshed / re-authed) token.
 * A 401 here is an authorization issue (RLS / permissions), not session
 * expiry — fresh credentials cannot fix it, so it throws `Unauthorized`
 * without popping the modal. Any other retry failure propagates accurately
 * instead of being converted into session expiry (which used to clear a
 * valid token — the 2026-09-11 CloudWatch finding).
 */
async function retryWithFreshToken<T>(
  path: string,
  options: RequestInit,
  headers: Record<string, string>,
  gen: number,
  parse: Parse<T> = parseJson,
): Promise<T> {
  // A sign-out between the refresh and this retry invalidates the retry: never
  // replay the original request (possibly a write) under stale credentials.
  if (gen !== authGeneration) {
    throw new Error('Session expired');
  }
  if (!accessToken) {
    // Unreachable through the wired flows (refresh/re-auth always sets the
    // token first) — fail loudly here rather than sending `Bearer null` and
    // misdiagnosing the fallout as an authorization issue.
    throw new Error('Session expired (missing token after refresh)');
  }
  headers['Authorization'] = `Bearer ${accessToken}`;
  const retry = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  checkGeneration(gen);
  if (retry.ok) {
    const data = retry.status === 204 ? (undefined as unknown as T) : await parse(retry);
    checkGeneration(gen);
    return data;
  }
  // Thrown as ApiResponseError(401) so status-aware callers (saveRetry,
  // toasts) treat it as non-retryable 4xx; the message stays the plain
  // 'Unauthorized' string existing handlers match on.
  if (retry.status === 401) {
    throw new ApiResponseError('Unauthorized', 401, null, null);
  }
  const error = await readApiResponseError(retry);
  checkGeneration(gen);
  throw error;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  parse: Parse<T> = parseJson,
): Promise<T> {
  if (signedOut) throw new Error('Session expired');
  // Capture the session generation for this request. Any sign-out bumps it,
  // and every later step (refresh, retry) refuses to act across the change.
  const gen = authGeneration;

  // Gate: if a reauth flow is in progress, wait for it before firing this request.
  // Prevents pollers from queuing 401s against a closed session.
  if (pendingReauth) {
    const reauthed = await waitWithSignal(pendingReauth, options.signal);
    if (!reauthed || gen !== authGeneration) {
      // Match the originating request's cancel path: the session is gone, so
      // clear the token rather than leaving a stale one gated behind the modal.
      if (gen === authGeneration) accessToken = null;
      throw new Error('Session expired');
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const resp = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  checkGeneration(gen);
  if (resp.ok) {
    // 204 No Content — nothing to parse
    if (resp.status === 204) {
      return undefined as unknown as T;
    }
    const data = await parse(resp);
    checkGeneration(gen);
    return data;
  }

  if (resp.status !== 401) {
    const error = await readApiResponseError(resp);
    checkGeneration(gen);
    // UX-5d: preserve the server-side typed `code` so callers can
    // distinguish (e.g.) `extraction_already_running` from a generic
    // 500. The Error message keeps the human-readable string for
    // toast/banner display. The full body is also retained so routes
    // can return structured advice (e.g. `advice` on graph-search 504).
    throw error;
  }

  // 401: try refresh once (deduplicated across concurrent requests). Note we
  // attempt this even when accessToken is null — the refresh cookie may still
  // be valid after a transient blip cleared the in-memory token (#294 F2, #110).
  // Reject a response from the ended session BEFORE refresh can capture the
  // new generation and install credentials while logout is still pending.
  if (gen !== authGeneration) {
    throw new Error('Session expired');
  }
  const outcome = await waitWithSignal(deduplicatedRefresh(), options.signal);

  // Sign-out (or re-auth) happened while we were refreshing — do not revive
  // the old session or replay the request.
  if (gen !== authGeneration) {
    throw new Error('Session expired');
  }

  if (outcome.kind === 'transient') {
    // Don't clear token. Don't pop modal. Surface a retryable, user-facing
    // error; the next request retries refresh after the shared backoff.
    throw new Error(TRANSIENT_REFRESH_MESSAGE);
  }

  if (outcome.kind === 'success') {
    return retryWithFreshToken<T>(path, options, headers, gen, parse);
  }

  // outcome.kind === 'expired' → real session expiry → modal flow.
  const reauthed = await waitWithSignal(reauthOrWait(), options.signal);
  if (gen !== authGeneration) {
    throw new Error('Session expired');
  }
  if (reauthed) {
    return retryWithFreshToken<T>(path, options, headers, gen, parse);
  }
  accessToken = null;
  throw new Error('Session expired');
}

async function doRefresh(gen: number): Promise<RefreshOutcome> {
  if (signedOut || gen !== authGeneration) return { kind: 'expired' };
  let outcome: RefreshOutcome;
  try {
    outcome = await authRequest('/auth/refresh', {method: 'POST', credentials: 'include'}, async resp => {
      if (gen !== authGeneration) return {kind: 'expired'};
      if (resp.status === 401) return {kind: 'expired'};
      if (!resp.ok) return {kind: 'transient'};
      const data = await resp.json();
      if (gen !== authGeneration) return {kind: 'expired'};
      if (typeof data?.access_token !== 'string' || !data.access_token) return {kind: 'transient'};
      const email = typeof data.email === 'string' ? data.email : tokenEmail(data.access_token);
      if (sessionEmail && !email) return {kind: 'transient'};
      if (sessionEmail && email?.toLowerCase().trim() !== sessionEmail) {
        // A different tab changed the shared cookie's account. Never replay an
        // old-account write or expose its mounted data to the new identity.
        invalidateAuth();
        resumeAfterReauth(false);
        onSessionChanged?.();
        return {kind: 'expired'};
      }
      accessToken = data.access_token;
      if (email) sessionEmail = email.toLowerCase().trim();
      return {kind: 'success', email};
    }, 10_000);
  } catch {
    outcome = { kind: 'transient' };
  }
  // Bound retry volume during an outage; reset on any definite outcome.
  if (gen === authGeneration) {
    refreshCooldownUntil = outcome.kind === 'transient' ? Date.now() + REFRESH_BACKOFF_MS : 0;
  }
  return outcome;
}

async function deduplicatedRefresh(): Promise<RefreshOutcome> {
  // Shared backoff: while cooling down after a transient failure, don't hit the
  // network again — every caller gets the same retryable outcome.
  if (Date.now() < refreshCooldownUntil) {
    return { kind: 'transient' };
  }
  // If a refresh is already in flight, wait for it instead of starting a new one
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = refreshAcrossTabs();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function refreshAcrossTabs(): Promise<RefreshOutcome> {
  const gen = authGeneration;
  try {
    return await withAuthCookieLock(() => doRefresh(gen));
  } catch {
    return {kind: 'transient'};
  }
}

// Public API

export const api = {
  get: <T>(path: string, opts?: { signal?: AbortSignal }) =>
    request<T>(path, { signal: opts?.signal }),

  post: <T>(path: string, body?: unknown, opts?: { signal?: AbortSignal }) =>
    request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      signal: opts?.signal,
    }),

  postBlob: (path: string, body?: unknown, opts?: { signal?: AbortSignal }) =>
    request<Blob>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      signal: opts?.signal,
    }, (response) => response.blob()),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

  patch: <T>(path: string, body?: unknown, opts?: { signal?: AbortSignal }) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined, signal: opts?.signal }),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /** Try to restore session from refresh token cookie. Returns email if successful. */
  tryRestore: async (): Promise<{ access_token: string; email: string } | null> => {
    if (signedOut) return null;
    const gen = authGeneration;
    const outcome = await deduplicatedRefresh();
    if (gen === authGeneration && outcome.kind === 'success' && accessToken && outcome.email) {
      return {access_token: accessToken, email: outcome.email};
    }
    return null;
  },
};
